import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, Copy, ExternalLink, QrCode } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal showing a QR code + shareable URL for the parent's live view.
 *
 * Coach clicks "Show parent live link" on a participant row → this modal
 * opens, calls POST /coach/assessment-sessions/{sid}/live-view (idempotent),
 * and renders a QR + the URL.
 *
 * Strategy: zero new dependencies. We render the QR via the open
 * api.qrserver.com PNG endpoint — small, fast, no JS payload. If you ever
 * want a JS-side library, this can swap to `qrcode.react` without changing
 * the API.
 *
 * Props:
 *   sessionId   — the assessment session id
 *   parentUid   — the parent's user_id (we generate one link per parent)
 *   parentName  — display label (e.g. "Sachin Mehta")
 *   onClose     — close handler
 */
export default function ParentLiveLinkModal({ sessionId, parentUid, parentName, onClose }) {
    const [liveId, setLiveId] = useState(null);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await axios.post(
                    `${API}/coach/assessment-sessions/${sessionId}/live-view`,
                    { parent_user_id: parentUid },
                    { withCredentials: true },
                );
                if (alive) {
                    setLiveId(r.data?.live_view_id || null);
                    setBusy(false);
                }
            } catch (e) {
                if (alive) {
                    toast.error("Couldn't generate live link", {
                        description: e?.response?.data?.detail,
                    });
                    setBusy(false);
                }
            }
        })();
        return () => { alive = false; };
    }, [sessionId, parentUid]);

    const origin = window.location.origin;
    const liveUrl = liveId ? `${origin}/live/${liveId}` : "";
    const qrSrc = liveUrl
        ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&color=FFFFFF&bgcolor=0A0A0A&data=${encodeURIComponent(liveUrl)}`
        : "";

    const copy = () => {
        if (!liveUrl) return;
        try {
            navigator.clipboard.writeText(liveUrl);
            toast.success("Link copied");
        } catch {
            toast.error("Couldn't copy — long-press to copy from the field");
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center px-4"
            data-testid="parent-live-link-modal"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-md bg-zinc-950 border border-zinc-800 p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-zinc-400 hover:text-white p-1"
                    aria-label="Close"
                    data-testid="parent-live-link-close"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-2 mb-1">
                    <QrCode className="w-4 h-4 text-brand-red" />
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">Live Parent View</div>
                </div>
                <h2 className="font-display text-2xl tracking-wider text-white mb-1">
                    {parentName || "Parent"}
                </h2>
                <p className="text-xs text-zinc-400 mb-5 leading-relaxed">
                    Have them scan this on their phone. They'll see only their athlete's results — updated live.
                </p>

                {busy ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
                    </div>
                ) : !liveId ? (
                    <div className="text-center text-sm text-zinc-300 py-8">
                        Couldn't generate the link. Try again or send the dashboard view instead.
                    </div>
                ) : (
                    <>
                        <div className="bg-zinc-900 border border-zinc-800 p-4 flex items-center justify-center mb-4">
                            <img
                                src={qrSrc}
                                width={260}
                                height={260}
                                alt="Live view QR code"
                                data-testid="parent-live-link-qr"
                                className="block"
                            />
                        </div>
                        <div className="flex items-stretch gap-2 mb-4">
                            <input
                                type="text"
                                readOnly
                                value={liveUrl}
                                onFocus={(e) => e.target.select()}
                                data-testid="parent-live-link-url"
                                className="flex-1 bg-black border border-zinc-800 px-3 py-2 text-xs text-zinc-200 font-mono focus:outline-none focus:border-brand-red"
                            />
                            <button
                                type="button"
                                onClick={copy}
                                data-testid="parent-live-link-copy"
                                title="Copy link"
                                className="px-3 border border-zinc-800 text-zinc-300 hover:text-white hover:border-brand-red"
                            >
                                <Copy className="w-3.5 h-3.5" />
                            </button>
                            <a
                                href={liveUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open preview"
                                className="px-3 border border-zinc-800 text-zinc-300 hover:text-white hover:border-brand-red flex items-center"
                            >
                                <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </div>
                        <div className="text-[11px] text-zinc-500 leading-relaxed">
                            Pricing reveals automatically after 20 minutes. The link auto-expires 24 hours after you mark the session complete.
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
